import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import MathJax from "react-mathjax2";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";

interface CustomReactMarkDownProps {
  content: string;
  role?: string;
  compact?: boolean; // when true, reduce paragraph margins for tight layouts
}

const CustomReactMarkDown: React.FC<CustomReactMarkDownProps> = React.memo(
  ({ content, role, compact = false }) => {
    const theme = useTheme();

    const isLessThanSmall: boolean = useMediaQuery(
      theme.breakpoints.down("sm"),
    );
    const isLessThanLarge: boolean = useMediaQuery(
      theme.breakpoints.down("lg"),
    );

    // Memoize fontSize to prevent recalculation
    const fontSize: string = React.useMemo(() => {
      return isLessThanSmall ? "0.8rem" : isLessThanLarge ? "0.9rem" : "1rem";
    }, [isLessThanSmall, isLessThanLarge]);

    // Memoize components to prevent recreation
    const components = React.useMemo(
      () => ({
        p(props: any) {
          const { children } = props;
          // This regex matches a string that contains a LaTeX expression
          // LaTeX expressions are wrapped in square brackets.
          // For example: [x^2]
          const regex = /\\?\[(\\?[^\\]|\\\\[\[\]])*\\?\]/g;
          const match =
            typeof children === "string" ? children.match(regex) : null;
          return match ? (
            <Box fontSize={"12px"} overflow={"scroll"}>
              <Grid size={{ xs: 1, lg: 1 }}>
                <MathJax.Context input="tex">
                  <MathJax.Node>{children}</MathJax.Node>
                </MathJax.Context>
              </Grid>
            </Box>
          ) : role === "user" ? (
            // Margins are left to the stylesheet (1em 0 by default), so the
            // bubble around a user's message can drop the outer ones.
            <p style={{ fontSize: fontSize }}>{children}</p>
          ) : (
            <Box
              component="p"
              sx={{
                fontSize: fontSize,
                // Inline formatting must not introduce a default paragraph
                // top margin and shift the first line below the avatar.
                margin: compact ? 0 : "0 0 1em",
                "&:last-child": { marginBottom: 0 },
              }}
            >
              {children}
            </Box>
          );
        },
        a: ({ node, ...props }: any) => {
          // Open YouTube links in a new tab
          const href = props.href || "";
          const isYouTubeLink =
            href.includes("youtube.com") || href.includes("youtu.be");

          if (isYouTubeLink) {
            return <a {...props} target="_blank" rel="noopener noreferrer" />;
          }
          return <a {...props} />;
        },
      }),
      [fontSize, role, compact],
    );

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for React.memo - only re-render if content, role or compact actually changed
    return (
      prevProps.content === nextProps.content &&
      prevProps.role === nextProps.role &&
      prevProps.compact === nextProps.compact
    );
  },
);

export default CustomReactMarkDown;
